import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { map, switchMap, take } from 'rxjs/operators';

import { CmsPageSection, PageTemplate, Suburb } from '../../../../models/content';
import { AccountService } from '../../../../services/account.service';
import { PicalunaSiteService } from '../../../../services/picaluna-site.service';
import {
  ReadinessDetail,
  SuburbReadinessService,
} from '../../../../services/suburb-readiness.service';
import { UserService } from '../../../../services/user.service';
import { templatePublishScope } from '../content/suburb-page';
import { insertionsAfter, TemplateKey, templateKeyFor } from '../template/suburb-template';
import { templateForSave } from '../template/template-blocks';
import { ContentRecordsService } from './content-records.service';

/**
 * The Suburb and Sub-region page templates (#262): read, saved, created from the defaults (Developer), published, and
 * previewed as one page. The editor is template/page-template-editor.component.ts, opened by PageEditorService.
 */
@Injectable()
export class PageTemplatesService {
  constructor(
    private records: ContentRecordsService,
    private account: AccountService,
    private readiness: SuburbReadinessService,
    private site: PicalunaSiteService,
    private users: UserService
  ) {}

  /** The template as stored, or null before it has been created. */
  template(key: TemplateKey): PageTemplate | null {
    return this.records.template(key);
  }

  /** Creating the templates from the defaults is a Developer action (the server refuses anyone else). */
  get canCreate(): boolean {
    return this.users.userSourceValue?.isDeveloper() === true;
  }

  /** Write whichever default template is missing, then re-read the account so it is cached. */
  createFromDefaults(): Observable<unknown> {
    return this.readiness
      .seedTemplates()
      .pipe(switchMap(() => this.account.get(true).pipe(take(1))));
  }

  /**
   * Save a template from its editor: the editor's sections onto the template read again just now, so a block someone
   * else added after this editor opened is kept (templateForSave). The server keeps a block a save leaves out as a
   * removed one, so a save from the copy the editor opened would remove theirs from every suburb page. The account is
   * re-read by the write.
   */
  save(
    key: TemplateKey,
    opened: PageTemplate | null,
    fields: { name: string; title: string; description: string },
    sections: Record<string, CmsPageSection>
  ): Observable<PageTemplate> {
    return this.account.get(true).pipe(
      take(1),
      map(
        () =>
          templateForSave(key, { opened, current: this.records.template(key) }, fields, sections)
            .template
      ),
      switchMap((template) =>
        this.account.patchContentItem('pageTemplates', template).pipe(
          take(1),
          map(() => this.records.template(key) ?? template)
        )
      )
    );
  }

  /** Records with blocks placed after these template blocks, and how many blocks (before a removal is saved). */
  anchoredTo(key: TemplateKey, blockIds: string[]): { records: number; blocks: number } {
    const ofKind = this.records.suburbs().filter((r) => templateKeyFor(r) === key);
    return insertionsAfter(ofKind, blockIds);
  }

  /** How many published pages a template's publish changes (the server's count). */
  publishedPages(key: TemplateKey): Observable<number> {
    return this.readiness
      .list({ kind: key, limit: 1 })
      .pipe(map((page) => page.meta.templates[key]?.publishedPages ?? 0));
  }

  publish(key: TemplateKey): Observable<unknown> {
    return this.site.publish(templatePublishScope(key)).pipe(take(1));
  }

  /** The pages built from a template, for "Preview as". */
  previewOptions(key: TemplateKey): Array<{ label: string; value: string }> {
    const regions = new Map(this.records.regions().map((r) => [r._meta.id, r.name]));
    return this.records
      .suburbs()
      .filter((r: Suburb) => templateKeyFor(r) === key && !!r.name)
      .map((r) => ({
        label: `${r.name} (${regions.get(r.regionId) ?? r.regionId})`,
        value: r._meta.id,
      }))
      .sort((a, b) => a.label.localeCompare(b.label));
  }

  /** One page as the site builds it from the saved template: each block, shown or hidden and why. */
  preview(recordId: string): Observable<ReadinessDetail> {
    return this.readiness.detail(recordId);
  }
}
